import { useRouter } from 'next/router'
import { useCallback, useEffect, useState } from 'react'
import { fontFamily } from 'tailwindcss/defaultTheme'

import { useStore } from '@/contexts/store'
import { calendas, inter } from '@/contexts/theme/Theme'

declare global {
  // eslint-disable-next-line no-unused-vars
  interface Window {
    ShopifyBuy: any
  }
}

type BuyButtonSDKProps = {
  id: string
  node: string
}

const shopifyClients: Record<string, any> = {}

const BuyButtonSDK = ({ id, node }: BuyButtonSDKProps) => {
  const [shopifyInit, setShopifyInit] = useState(false)
  const { strings } = useStore()
  const { locale } = useRouter()

  const handleScriptLoad = useCallback(() => {
    setShopifyInit(true)
  }, [])

  useEffect(() => {
    if (!strings?.setup?.js_store_snippet) return

    const existingScript = document.querySelector(
      `script[src="${strings.setup.js_store_snippet}"]`
    )
    if (existingScript) {
      if (window.ShopifyBuy) {
        setShopifyInit(true)
      } else {
        existingScript.addEventListener('load', handleScriptLoad)
      }
    } else {
      const script = document.createElement('script')
      script.src = strings.setup.js_store_snippet
      script.onload = handleScriptLoad
      document.head.appendChild(script)
    }
  }, [strings?.setup?.js_store_snippet, handleScriptLoad])

  useEffect(() => {
    if (!window.ShopifyBuy) return
    setShopifyInit(true)
  }, [])

  useEffect(() => {
    if (!shopifyInit || !strings?.setup?.client_domain) return

    const nodeElement = document.getElementById(node)
    if (!nodeElement) return

    const ShopifyBuy = window.ShopifyBuy

    let ShopifyBuyUI: any = undefined

    const clientLanguage = locale || 'it'

    if (!shopifyClients[clientLanguage]) {
      shopifyClients[clientLanguage] = ShopifyBuy.buildClient({
        domain: strings?.setup?.client_domain,
        storefrontAccessToken: strings?.setup?.api_key,
        language: clientLanguage
      })
    }

    const shopifyClient = shopifyClients[clientLanguage]

    ShopifyBuy.UI.onReady(shopifyClient).then(function (ui: any) {
      ShopifyBuyUI = ui
      ShopifyBuyUI.createComponent('product', {
        id,
        node: nodeElement,
        moneyFormat: strings?.client_data?.formato_valuta,
        options: {
          product: {
            iframe: false,
            contents: {
              img: false,
              title: false,
              price: false,
              options: false,
              description: false,
              button: true
            },
            templates: {
              button: `<button class={{data.classes.product.button}}><span data-label="${strings?.shopify_strings.product.button}"><span>${strings?.shopify_strings.product.button}</span></span></button>`
            },
            classes: {
              button: 'shopify-product-button'
            },
            styles: {
              product: {
                '@media (min-width: 601px)': {
                  'max-width': 'calc(25% - 20px)',
                  'margin-left': '20px',
                  'margin-bottom': '50px'
                }
              },
              button: {
                ':hover': {
                  'background-color': '#a67b07'
                },
                'background-color': '#b88908',
                'border-radius': '0px',
                ':focus': {
                  'background-color': '#a67b07'
                }
              }
            },
            buttonDestination: 'modal',
            text: {
              button: strings?.shopify_strings.product.button
            }
            /* events: {
              addVariantToCart: (product: any) => {
                console.log('addVariantToCart', product)
              },
              updateQuantity: (product: any) => {
                console.log('updateQuantity', product)
              },
              openModal: (product: any) => {
                console.log('openModal', product)
              },
              openOnlineStore: (product: any) => {
                console.log('openOnlineStore', product)
              },
              openCheckout: (product: any) => {
                console.log('openCheckout', product)
              }
            } */
          },
          productSet: {
            styles: {
              products: {
                '@media (min-width: 601px)': {
                  'margin-left': '-20px'
                }
              }
            }
          },
          modalProduct: {
            contents: {
              img: false,
              description: true,
              imgWithCarousel: true,
              button: false,
              buttonWithQuantity: true
            },
            styles: {
              title: {
                'font-family': `${calendas.style.fontFamily}, ${fontFamily.serif}`,
                'font-size': '2.104rem',
                'line-height': '100%',
                'letter-spacing': '0rem',
                'font-weight': '400'
              },
              price: {
                'font-family': `${inter.style.fontFamily}, ${fontFamily.sans}`,
                'font-size': '14px',
                'line-height': '150%',
                'letter-spacing': '0.32px',
                'font-weight': '400'
              },
              product: {
                '@media (min-width: 601px)': {
                  'max-width': '100%',
                  'margin-left': '0px',
                  'margin-bottom': '0px'
                }
              },
              button: {
                ':hover': {
                  'background-color': '#a67b07'
                },
                'background-color': '#b88908',
                ':focus': {
                  'background-color': '#a67b07'
                }
              },
              description: {
                'font-family': `${inter.style.fontFamily}, ${fontFamily.sans}`,
                'font-size': '14px',
                'line-height': '150%',
                'letter-spacing': '0.32px',
                'font-weight': '400'
              }
            },
            text: {
              button: strings?.shopify_strings.modal.button,
              outOfStock: strings?.shopify_strings.modal.outOfStock,
              unavailable: strings?.shopify_strings.modal.unavailable
            }
            /* events: {
              closeModal: (modal: any) => {
                console.log('closeModal', modal)
              }
            } */
          },
          option: {},
          cart: {
            iframe: true,
            popup: false,
            styles: {
              cart: {
                'font-family': `${inter.style.fontFamily}, ${fontFamily.sans}`,
                'background-color': '#f4f2f1'
              },
              title: {
                'font-family': `${inter.style.fontFamily}, ${fontFamily.sans}`,
                'font-size': '1.6rem',
                'line-height': '100%',
                'letter-spacing': '0rem',
                'font-weight': '700'
              },
              footer: {
                'background-color': '#f4f2f1'
              },
              button: {
                'font-family': `${inter.style.fontFamily}, ${fontFamily.sans}`,
                'border-radius': '0px',
                ':hover': {
                  'background-color': '#a67b07'
                },
                'background-color': '#b88908',
                ':focus': {
                  'background-color': '#a67b07'
                }
              }
            },
            text: {
              title: strings?.shopify_strings.cart.title,
              empty: strings?.shopify_strings.cart.empty,
              button: strings?.shopify_strings.cart.button,
              total: strings?.shopify_strings.cart.total,
              currency: strings?.shopify_strings.cart.currency,
              notice: strings?.shopify_strings.cart.notice,
              noteDescription: strings?.shopify_strings.cart.note_description
            }
            /* events: {
              openCheckout: (cart: any) => {
                console.log('openCheckout', cart)
              },
              updateItemQuantity: (cart: any) => {
                console.log('updateItemQuantity', cart)
              }
            } */
          },
          lineItem: {
            styles: {
              itemTitle: {
                'font-family': 'var(--font-family-base)',
                'font-size': '1.2rem'
              },
              price: {},
              quantityDecrement: {
                'border-color': 'rgba(181,139,30,0.3)'
              },
              quantityInput: {
                'border-color': 'rgba(181,139,30,0.3)'
              },
              quantityIncrement: {
                'border-color': 'rgba(181,139,30,0.3)'
              }
            }
          },
          toggle: {
            styles: {
              toggle: {
                'border-radius': '0px',
                'z-index': '30',
                'font-family': 'var(--font-family-base)',
                'background-color': '#b88908',
                ':hover': {
                  'background-color': '#a67b07'
                },
                ':focus': {
                  'background-color': '#a67b07'
                }
              }
            }
          }
        }
      })
    })

    return () => {
      if (ShopifyBuyUI) {
        ShopifyBuyUI.destroyComponent('product')
        ShopifyBuyUI.destroyComponent('toggle')
        ShopifyBuyUI.destroyComponent('modal')
        ShopifyBuyUI.destroyComponent('cart')
      }
    }
  }, [id, node, strings, shopifyInit, locale])

  return null
}

export default BuyButtonSDK
